import { useRouter } from 'next/router';
import { toast } from 'react-hot-toast';

import { useInvalidateCadenceState } from '@/hooks/trpc/useCadenceMutation';
import { useInvalidateDoneForYouReplyLists } from '@/hooks/trpc/useSuperadminMutation';
import { api } from '@/utils/api';

export const useVerifyContactEmailMutation = () => {
  const utils = api.useUtils();
  return api.contact.verifyEmail.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
    },
  });
};

export const useMarkPhoneFirmNumberMutation = () => {
  const utils = api.useUtils();
  return api.contact.markPhoneFirmNumber.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
    },
  });
};

export const useRemoveContactFieldValueMutation = () => {
  const utils = api.useUtils();
  return api.contact.deleteValue.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
    },
  });
};

export const useReassignContactPhoneToOrgMutation = () => {
  const utils = api.useUtils();
  return api.contact.reassignPhoneToOrg.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
      // The number now lives on the contact's org — refresh org queries so the org detail page
      // shows the newly recorded firm line.
      void utils.org.id.invalidate();
    },
  });
};

export const useUpdateContactMutation = () => {
  const utils = api.useUtils();
  return api.contact.update.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
    },
  });
};

export const useRemoveContactFieldPrimaryMutation = () => {
  const utils = api.useUtils();
  return api.contact.unsetPrimaryField.useMutation({
    onSuccess: () => {
      void utils.contact.byId.invalidate();
    },
  });
};

export const useMakeContactFieldPrimaryMutation = () => {
  const utils = api.useUtils();
  return api.contact.setPrimaryField.useMutation({
    onSuccess: () => {
      void utils.contact.byId.invalidate();
    },
  });
};

export const useGenerateDossiersBulkMutation = () => {
  return api.contact.generateDossiersBulk.useMutation();
};

export const useExportContactsCSVMutation = () => {
  return api.contact.exportContactsCSV.useMutation();
};

export const useRegenerateDossierMutation = () => {
  const utils = api.useUtils();
  return api.contact.regenerateDossierCtx.useMutation({
    // The mutation now resolves with a discriminated result instead of throwing
    // on a logical failure, so only refetch when generation actually succeeded.
    // A failed attempt is surfaced by the caller as a persistent error card.
    onSuccess: (data) => {
      if (!data.ok) return;
      void utils.contactDetailBootstrap.forContact.invalidate();
      if (!data.profileFetched) return;
      // Regenerating can now pull the LinkedIn profile first for a contact that
      // never had one, which rewrites name/title/company/photo and stamps
      // contact_user.last_unipile_fetch_at. Same invalidations
      // useEnhanceContactMutation does for the identical side effect — without
      // them the card keeps showing the old CSV name and the contact stays on
      // the /contacts "never Unipile enhanced" list until a hard reload.
      void utils.contact.byId.invalidate();
      void utils.contact.search.invalidate();
      void utils.contact.neverUnipileEnhanced.invalidate();
    },
  });
};

export const useEnhanceContactMutation = () => {
  const utils = api.useUtils();
  return api.contact.enhance.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
      void utils.team.id.invalidate();
      void utils.cadence.cadencesForCards.invalidate();
      void utils.contact.search.invalidate();
      // Re-arm the enrichment indicator poll for the freshly queued run.
      void utils.contact.enrichmentStatus.invalidate();
      // Enriching stamps contact_user.last_unipile_fetch_at, so the contact is no
      // longer "never Unipile enhanced" — drop it from that /contacts list.
      void utils.contact.neverUnipileEnhanced.invalidate();
      // Enhance can kick off a phone re-enrichment search (waterfall) in the
      // background. The Bad Numbers page only polls while a visible row is
      // searching — without this refetch it never learns the search started,
      // so the found number would only appear after a manual reload.
      void utils.phoneReEnrichment.badNumberContacts.invalidate();
      if (data.deleted) {
        // Enhance found the LinkedIn profile dead & unrepairable and removed the contact
        // server-side. Drop it from the board/stage/connected surfaces too, since Enhance
        // is reachable from those selections.
        void utils.contact.getConnected.invalidate();
        void utils.contact.getConnectedGroupedByOrg.invalidate();
        void utils.stage.withContacts.invalidate();
      }
    },
  });
};

export const useContactSetStageMutation = () => {
  const utils = api.useUtils();
  const invalidateDoneForYouReplyLists = useInvalidateDoneForYouReplyLists();
  return api.contact.setStage.useMutation({
    onSuccess: () => {
      void utils.contact.byId.invalidate();
      void utils.contact.byOrg2.invalidate();
      void utils.superAdmin.contact.invalidate();
      void utils.sharing.paginated.invalidate();
      void utils.stage.all.invalidate();
      void utils.stage.withOrgs.invalidate();
      void utils.stage.withContacts.invalidate();
      void utils.contact.getConnectedGroupedByOrg.invalidate();
      void utils.contact.getConnected.invalidate();
      void utils.history.contact.invalidate();
      void invalidateDoneForYouReplyLists();
      void utils.contact.search.invalidate();
      void utils.contact.interrupted.invalidate();
      void utils.globalReminder.paginated.invalidate();
    },
  });
};

export const useContactBulkSetStageMutation = () => {
  const utils = api.useUtils();
  const invalidateDoneForYouReplyLists = useInvalidateDoneForYouReplyLists();
  return api.contact.bulkSetStage.useMutation({
    onSuccess: () => {
      void invalidateDoneForYouReplyLists();
      void utils.contact.byId.invalidate();
      void utils.contact.byOrg2.invalidate();
      void utils.contact.getConnected.invalidate();
      void utils.contact.getConnectedGroupedByOrg.invalidate();
      void utils.stage.withContacts.invalidate();
      void utils.stage.withOrgs.invalidate();
      // A stage change is logged to the contact history panel (parity with setStage).
      void utils.history.contact.invalidate();
      void utils.contact.search.invalidate();
      void utils.contact.interrupted.invalidate();
      void utils.globalReminder.paginated.invalidate();
    },
  });
};

export const useContactAckContentViewAndResponse = () => {
  const utils = api.useUtils();
  return api.contact.ackContentViewAndResponse.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
      void utils.contact.contentViewed.invalidate();
      void utils.contact.responded.invalidate();
      void utils.globalReminder.paginated.invalidate();
    },
  });
};

export const useContactBulkAckInteraction = () => {
  const utils = api.useUtils();
  return api.contact.bulkAckInteraction.useMutation({
    onSuccess: () => {
      void utils.contact.byId.invalidate();
      void utils.contact.contentViewed.invalidate();
      void utils.contact.responded.invalidate();
      void utils.globalReminder.paginated.invalidate();
    },
  });
};

export const useContactSetTrackingMutation = () => {
  const utils = api.useUtils();
  return api.contact.setTracked.useMutation({
    onMutate: ({ contactId, data: { tracked } }) => {
      utils.contact.byId.setData({ contactId }, (data) =>
        Object.assign(
          {},
          data,
          data?.contactV2 ? { contactV2: Object.assign({}, data.contactV2, { tracked }) } : undefined,
        ),
      );
    },
    onError: () => {
      void utils.contact.invalidate();
    },
  });
};

export const useContactSetDisqualifiedMutation = () => {
  const utils = api.useUtils();
  return api.contact.setDisqualified.useMutation({
    onMutate: ({ contactId, disqualified }) => {
      utils.contact.byId.setData({ contactId }, (data) =>
        Object.assign(
          {},
          data,
          data?.authedUser ? { authedUser: Object.assign({}, data.authedUser, { disqualified }) } : undefined,
        ),
      );
    },
    onSuccess: () => {
      void utils.contact.invalidate();
    },
    onError: () => {
      void utils.contact.invalidate();
    },
  });
};

export const useMarkInteractionAsMeetingBookedMutation = () => {
  const utils = api.useUtils();
  const invalidateDoneForYouReplyLists = useInvalidateDoneForYouReplyLists();
  return api.contact.markInteractionAsMeetingBooked.useMutation({
    onSuccess: () => {
      // Marking interest writes the prefilter row the Received tab renders.
      void invalidateDoneForYouReplyLists();
      void utils.contact.byId.invalidate();
      void utils.contact.invalidate();
      void utils.history.contact.invalidate();
      void utils.superAdmin.contactHistory.invalidate();
      void utils.contactDetailBootstrap.forContact.invalidate();
    },
  });
};

/** Re-read one email from the mailbox it came from; the history shows the body it stored. */
export const useResyncEmailMutation = () => {
  const utils = api.useUtils();
  return api.history.resyncEmail.useMutation({
    onSuccess: () => {
      void utils.history.contact.invalidate();
      // The latest-reply banner reads the same row off the contact.
      void utils.contact.byId.invalidate();
    },
  });
};

export const useDeleteMeetingBookedMutation = () => {
  const utils = api.useUtils();
  return api.contact.deleteMeetingBooked.useMutation({
    onSuccess: (data, variables) => {
      void utils.contact.getMeetingBookedDetails.invalidate({ contactId: variables.contactId });
      void utils.contact.byId.invalidate({ contactId: variables.contactId });
      void utils.history.contact.invalidate();
      void utils.contactDetailBootstrap.forContact.invalidate({ contactId: variables.contactId });
      if (data.warning) {
        toast(data.warning, { icon: '⚠️' });
      }
    },
  });
};

export const useContactSetMinAgeOverrideMutation = () => {
  const utils = api.useUtils();
  return api.contact.setMinAgeOverride.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
    },
  });
};

export const useContactResetGenderMutation = () => {
  const utils = api.useUtils();
  return api.contact.resetGender.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
    },
  });
};

export const useResearchFromLinkedInMutation = () => {
  const utils = api.useUtils();
  const router = useRouter();
  return api.contact.researchFromLinkedIn.useMutation({
    onSuccess: (data, variables) => {
      void utils.contact.byId.invalidate();
      void utils.contact.search.invalidate();
      void utils.contact.getConnected.invalidate();
      void utils.contact.getConnectedGroupedByOrg.invalidate();
      void utils.contactDetailBootstrap.forContact.invalidate();
      void utils.cadence.cadencesForCards.invalidate();
      // Refresh-from-LinkedIn does a Unipile fetch (stamps last_unipile_fetch_at) and
      // can merge duplicates away, so the "never Unipile enhanced" list can go stale.
      void utils.contact.neverUnipileEnhanced.invalidate();

      const mergedCount = data.mergedFromContactPublicIds?.length ?? 0;
      if (mergedCount === 0) return;

      const total = mergedCount + 1;
      toast.success(`Merged ${total} duplicate contacts into one`);

      // The contact in the URL was merged away — redirect to the surviving winner
      // so the user doesn't end up on a now-hidden (`merged_into IS NOT NULL`) page.
      const urlContactId =
        typeof router.query.contactId === 'string' ? router.query.contactId : undefined;
      if (urlContactId === variables.contactId && data.winnerContactPublicId !== variables.contactId) {
        void router.replace({
          pathname: router.pathname,
          query: { ...router.query, contactId: data.winnerContactPublicId },
        });
      }
    },
  });
};

export const useDeleteForeignMappingMutation = () => {
  const utils = api.useUtils();
  return api.contact.deleteForeignMapping.useMutation({
    onSuccess: (_, variables) => {
      void utils.contact.getForeignMappingsAll.invalidate({ contactId: variables.contactId });
      void utils.contact.byId.invalidate({ contactId: variables.contactId });
      void utils.contactDetailBootstrap.forContact.invalidate();
    },
  });
};

export const useAddForeignMappingMutation = () => {
  const utils = api.useUtils();
  const invalidateCadenceState = useInvalidateCadenceState();
  return api.contact.addForeignMapping.useMutation({
    onSuccess: (data, variables) => {
      void utils.contact.getForeignMappingsAll.invalidate({ contactId: variables.contactId });
      // The mapping reactivated one of the contact's campaigns. The full campaign refresh covers the
      // contact and its detail bootstrap too.
      if (data.reactivated) {
        void invalidateCadenceState();
        return;
      }
      void utils.contact.byId.invalidate({ contactId: variables.contactId });
      void utils.contactDetailBootstrap.forContact.invalidate();
    },
  });
};

export const useVerifyForeignMappingMutation = () => {
  const utils = api.useUtils();
  return api.contact.verifyForeignMapping.useMutation({
    onSuccess: (_, variables) => {
      void utils.contact.getForeignMappingsAll.invalidate({ contactId: variables.contactId });
      void utils.contact.byId.invalidate({ contactId: variables.contactId });
      void utils.contactDetailBootstrap.forContact.invalidate({ contactId: variables.contactId });
    },
  });
};